import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material"

import { chatApi, clientApi, projectApi, userApi } from "../../api"
import { usePermissions } from "../../permissions"
import { avatarColor, initialsOf } from "../../utils/format"
import ClientSelect from "../../components/ClientSelect"

/**
 * Starting a conversation.
 *
 * Two shapes, because they are genuinely different things:
 *
 *   DIRECT   pick a person. Idempotent on the server — asking twice returns the
 *            same thread, so a UI that opens a DM on every click of a name does
 *            not scatter the history across empty duplicates.
 *
 *   TOPIC    the thing TallyWise adds over a plain DM: a NAMED thread with a
 *            client. "The August close" and "the new store rollout" are two
 *            conversations with the same people, each with its own unread count,
 *            instead of one scroll where last week's question is a thousand
 *            messages up. A topic is required, which is why it is the first field.
 */
/** Organization 0 is the back office — a tenant like any other, not a blank. */
const BACK_OFFICE_ORG_ID = "0"

export default function NewConversationDialog({ open, onClose, onCreated }) {
  const { can, isBackOffice, organizationId } = usePermissions()
  const canStartClientThread = can("chat", "create_client_thread")

  const [tab, setTab] = useState("dm")
  const [directory, setDirectory] = useState([])
  const [clients, setClients] = useState([])
  const [projects, setProjects] = useState([])

  const [peer, setPeer] = useState(null)
  const [topic, setTopic] = useState("")
  const [clientId, setClientId] = useState("")
  const [participants, setParticipants] = useState([])
  const [project, setProject] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    setError(null)
    setPeer(null)
    setTopic("")
    setParticipants([])
    setProject(null)
    setClientId(isBackOffice ? BACK_OFFICE_ORG_ID : organizationId || "")

    userApi
      .directory()
      .then((data) => setDirectory(data.users || []))
      .catch(() => setDirectory([]))

    // Only back office chooses a client — everybody else's threads belong to
    // their own organization, and the field would be a dropdown of one.
    if (isBackOffice && can("clients", "view")) {
      clientApi
        .list()
        .then((data) => setClients(data.organizations || []))
        .catch(() => setClients([]))
    }
  }, [open, isBackOffice, organizationId, can])

  useEffect(() => {
    if (!clientId) {
      setProjects([])
      return
    }
    projectApi
      .list({ client_id: clientId, limit: 200 })
      .then((data) => setProjects(data.projects || []))
      .catch(() => setProjects([]))
  }, [clientId])

  /* Participants must belong to the thread's client, or to the back office —
     the server enforces this and silently drops anyone else, so the picker is
     filtered rather than letting someone be chosen and quietly discarded. */
  const eligible = useMemo(() => {
    if (!clientId) return directory
    return directory.filter((u) => String(u.organization_id) === String(clientId) || String(u.organization_id) === "0")
  }, [directory, clientId])

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      if (tab === "dm") {
        if (!peer) throw new Error("Pick someone to message")
        const result = await chatApi.openDm(peer.id)
        onCreated(result.room)
        return
      }

      const result = await chatApi.createRoom({
        kind: "topic",
        client_id: clientId === "" ? undefined : clientId,
        topic: topic.trim(),
        participant_ids: participants.map((p) => p.id),
        project_id: project?.id ?? null,
      })
      onCreated(result.room)
    } catch (err) {
      setError(err.userMessage || err.message)
    } finally {
      setBusy(false)
    }
  }

  const canSubmit = tab === "dm" ? Boolean(peer) : Boolean(topic.trim())

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>New conversation</DialogTitle>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ px: 3 }}>
        <Tab value="dm" label="Direct message" />
        <Tab value="topic" label="Topic thread" disabled={isBackOffice && !canStartClientThread} />
      </Tabs>

      <DialogContent dividers>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {tab === "dm" ? (
          <Autocomplete
            options={directory}
            value={peer}
            onChange={(_, value) => setPeer(value)}
            getOptionLabel={(option) => option.name || option.email}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => {
              const { key, ...rest } = props
              return (
                <Box component="li" key={key} {...rest}>
                  <Avatar sx={{ width: 26, height: 26, mr: 1.5, fontSize: 11, bgcolor: avatarColor(option.name || option.email) }}>
                    {initialsOf(option.name || option.email)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {option.name || option.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {option.organization_label}
                      {option.role_label ? ` · ${option.role_label}` : ""}
                    </Typography>
                  </Box>
                </Box>
              )
            }}
            renderInput={(params) => <TextField {...params} label="Who do you want to message?" autoFocus />}
          />
        ) : (
          <Stack spacing={2.5}>
            <TextField
              label="Topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              fullWidth
              autoFocus
              required
              placeholder="August close"
              helperText="What this thread is about. It is how people tell it apart from the others with the same people in it."
            />

            {isBackOffice && clients.length > 0 ? (
              <ClientSelect
                clients={clients}
                value={clientId}
                onChange={(id) => {
                  setClientId(id)
                  // Participants and the project belong to the old client.
                  setParticipants([])
                  setProject(null)
                }}
                fullWidth
                helperText="Which client this conversation is about"
              />
            ) : null}

            <Autocomplete
              multiple
              options={eligible}
              value={participants}
              onChange={(_, value) => setParticipants(value)}
              getOptionLabel={(option) => option.name || option.email}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...rest } = getTagProps({ index })
                  return <Chip key={key} {...rest} size="small" label={option.name || option.email} />
                })
              }
              renderInput={(params) => (
                <TextField {...params} label="Participants" helperText="You are added automatically" />
              )}
            />

            <Autocomplete
              options={projects}
              value={project}
              onChange={(_, value) => setProject(value)}
              getOptionLabel={(option) => `${option.code} · ${option.name}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label="Attach a project (optional)" helperText="A task can be attached once the thread exists" />
              )}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit || busy}>
          {busy ? "Starting…" : "Start conversation"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
